import {
  eyeIcon,
  save,
  LoadIcon,
  PlusIcon,
  pencilIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React, { useState } from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";
import { spryboardAPI, extractSpryboardSaveFromResponse } from "../api/backend";
import { getActiveBoardContext, setActiveBoardContext } from "../data";

import { saveDebugState } from "./DebugCanvas";

const SaveToSpryDriveMenuItem: React.FC<{
  onSaveComplete?: (fileId?: string, boardId?: string | null, title?: string) => void;
  disabled?: boolean;
}> = ({ onSaveComplete, disabled = false }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);

  const handleSaveToSpryDrive = async () => {
    if (disabled) {
      return;
    }
    setIsSaving(true);
    setSaveStatus(null);

    try {
      // Get the Excalidraw API from window (set by App.tsx)
      const excalidrawAPI = (window as any).excalidrawAPI;
      if (!excalidrawAPI) {
        setSaveStatus("Editor not ready");
        setIsSaving(false);
        return;
      }

      const appState = excalidrawAPI.getAppState();
      const elements = excalidrawAPI.getSceneElements();
      const files = excalidrawAPI.getFiles();

      const boardData = {
        type: "excalidraw",
        version: 2,
        source: window.location.origin,
        elements,
        appState: {
          viewBackgroundColor: appState?.viewBackgroundColor || "#ffffff",
          gridSize: appState?.gridSize || null,
        },
        files: files || {},
      };

      const { boardId, driveFileId, title } = getActiveBoardContext();

      const finalFileName = (title || "Untitled Board").trim() || "Untitled Board";

      const response = await spryboardAPI.save({
        filename: finalFileName,
        boardData,
        fileId: driveFileId || undefined,
        boardId: boardId || undefined,
      });

      const savedData = extractSpryboardSaveFromResponse(response);
      const savedFileId = savedData?.fileId || savedData?.id;
      const savedBoardId = savedData?.boardId || boardId;

      if (savedFileId || savedBoardId) {
        setActiveBoardContext({
          boardId: savedBoardId,
          driveFileId: savedFileId,
          title: finalFileName,
        });
      }

      setSaveStatus("✓ Saved to SpryDrive!");
      onSaveComplete?.(savedFileId, savedBoardId, finalFileName);

      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error: any) {
      console.error("SpryDrive save error:", error);
      setSaveStatus(`Error: ${error.message || "Failed to save"}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MainMenu.Item
      icon={save}
      onClick={handleSaveToSpryDrive}
      data-testid="save-to-sprydrive"
      disabled={disabled}
    >
      {disabled ? "Save Board (View only)" : isSaving ? "Saving…" : saveStatus || "Save Board"}
    </MainMenu.Item>
  );
};

const NewBoardMenuItem: React.FC<{
  onNewBoard: () => void;
}> = ({ onNewBoard }) => {
  return (
    <MainMenu.Item
      icon={PlusIcon}
      onClick={onNewBoard}
      data-testid="new-board"
    >
      New Board
    </MainMenu.Item>
  );
};

const OpenFromSpryDriveMenuItem: React.FC<{
  onOpenBoard: () => void;
}> = ({ onOpenBoard }) => {
  return (
    <MainMenu.Item
      icon={LoadIcon}
      onClick={onOpenBoard}
      data-testid="open-from-sprydrive"
    >
      Open from SpryDrive
    </MainMenu.Item>
  );
};

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  onCreateBoard: () => void;
  onOpenPicker: () => void;
  onRenameBoard: () => void;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  isReadOnlyBoard?: boolean;
  canShareBoard?: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
}> = React.memo((props) => {
  return (
    <MainMenu>
      <NewBoardMenuItem onNewBoard={props.onCreateBoard} />
      <OpenFromSpryDriveMenuItem onOpenBoard={props.onOpenPicker} />
      <MainMenu.Item
        icon={pencilIcon}
        onClick={props.onRenameBoard}
        data-testid="rename-board"
        disabled={props.isReadOnlyBoard}
      >
        {props.isReadOnlyBoard ? "Rename Board (View only)" : "Rename Board"}
      </MainMenu.Item>
      <SaveToSpryDriveMenuItem disabled={props.isReadOnlyBoard} />
      <MainMenu.Separator />
      {props.isCollabEnabled && (props.canShareBoard ?? true) && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      {props.isCollabEnabled && (props.canShareBoard ?? true) && <MainMenu.Separator />}
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      <MainMenu.Separator />
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.Separator />
      <MainMenu.DefaultItems.ChangeCanvasBackground />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onClick={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.ToggleTheme
        allowSystemTheme
        theme={props.theme}
        onSelect={props.setTheme}
      />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
    </MainMenu>
  );
});
